/**
 * Cross-browser DatePickerField — replaces native <input type="date"> for
 * UX consistency. Built on the shadcn Calendar + Popover primitives.
 *
 * Two storage formats supported:
 *   format="iso"   → returns/accepts "yyyy-mm-dd" (default; matches the rest
 *                    of the app where dates are sent to the backend as ISO).
 *   format="au"    → returns/accepts "dd/mm/yyyy" (used on consent +
 *                    screening Date-of-Birth fields per Australian convention).
 *
 * Typed manual entry is supported alongside the calendar pop-over so users
 * who prefer the keyboard never get blocked.
 */
import { useState, useMemo, useEffect } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

function pad2(n) { return String(n).padStart(2, '0'); }

function parseISO(s) {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? undefined : d;
}

function parseAU(s) {
  if (!s) return undefined;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
  if (!m) return undefined;
  let yy = Number(m[3]);
  if (yy < 100) yy += yy < 30 ? 2000 : 1900; // crude windowing
  const d = new Date(yy, Number(m[2]) - 1, Number(m[1]));
  return isNaN(d.getTime()) ? undefined : d;
}

function fmtISO(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function fmtAU(d) {
  if (!d) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function DatePickerField({
  value, onChange, format = 'iso', testid,
  placeholder, className = '', inputClassName = '', disabled = false,
  yearRangeStart = 1900, yearRangeEnd = (new Date()).getFullYear() + 5,
}) {
  const parseFn = format === 'au' ? parseAU : parseISO;
  const fmtFn = format === 'au' ? fmtAU : fmtISO;
  const ph = placeholder || (format === 'au' ? 'dd/mm/yyyy' : 'yyyy-mm-dd');

  // Local typed text — kept in sync with `value` so external resets propagate.
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);

  const selected = useMemo(() => parseFn(text), [text, parseFn]);
  const [open, setOpen] = useState(false);

  return (
    <div className={`relative flex items-center gap-1 ${className}`} data-testid={testid && `${testid}-wrapper`}>
      <input
        type="text"
        value={text}
        onChange={e => {
          setText(e.target.value);
          // Push partial / complete typed value to parent only when it parses
          // cleanly; keeps the form state ISO/AU-formatted at all times.
          const parsed = parseFn(e.target.value);
          if (parsed) onChange(fmtFn(parsed));
          else if (!e.target.value) onChange('');
        }}
        placeholder={ph}
        disabled={disabled}
        data-testid={testid}
        className={`flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2 ${inputClassName}`}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid && `${testid}-calendar-btn`}
            title="Open calendar"
            className="shrink-0 p-2 rounded-lg border border-border hover:bg-accent disabled:opacity-50"
          >
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (d) {
                const out = fmtFn(d);
                setText(out);
                onChange(out);
                setOpen(false);
              }
            }}
            captionLayout="dropdown"
            fromYear={yearRangeStart}
            toYear={yearRangeEnd}
            defaultMonth={selected || new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
